import {
  slab, wallX, wallZ, goal, sign, neon, checkpoint, edgeLights, pad, lift, mover,
} from '../kit.js';

const deck = (z, y, w = 22, d = 26, m = 'plate') => [
  slab(0, y, z, w, d, m, 1.2),
  ...edgeLights(0, y, z, w, d, 'amber'),
];

/**
 * A time trial. Launch pads carry the course on their own; portals are the
 * fast line. Every jump is tuned so the pad alone lands you safely, which
 * keeps the level winnable while leaving the record to whoever cuts corners.
 */
export default {
  id: 'velocity',
  name: 'VELOCITY',
  blurb: 'Outrun the lockdown. Seventy five seconds.',
  mode: { type: 'gauntlet', limit: 75, par: 40 },
  redline: { seconds: 36 },   // docs/REDLINE.md; seeded from the board, honesty pass pending
  spawn: { p: [0, 0.4, 8], yaw: 0 },
  killY: -160,
  env: {
    fog: 0x24100a,
    fogDensity: 0.0040,
    sky: { zenith: 0x0c0406, horizon: 0x33110c, glow: 0xb03a10, aurora: 0x1f6f9a },
    floorY: -300,
  },
  objects: [
    // ---- start bay
    ...deck(0, 0, 24, 30),
    wallZ(0, 0, 15.7, 26, 12, 'panel', 1.4),
    neon(0, 12.2, 15.0, [24, 0.16, 0.16], 'amber'),
    pad(0, 0, -8, 40, [0, 0.56, -0.83]),

    // ---- hop 1
    ...deck(-64, 0, 24, 26),
    checkpoint(0, 0, -56),
    pad(0, 0, -70, 46, [0, 0.56, -0.83]),
    wallX(-13.4, 0, -64, 26, 10, 'panel', 1.4),

    // ---- hop 2, raised
    ...deck(-130, 10, 24, 26),
    checkpoint(0, 10, -122),
    pad(0, 10, -136, 40, [0, 0.58, -0.81]),
    wallX(13.4, 10, -130, 26, 12, 'panel', 1.4),
    mover(0, 10, -152, [0, 10, -170], [11, 0.8, 11], 5),

    // ---- lift tower, with a ring deck so the column stays clear
    ...deck(-200, 0, 26, 26),
    lift(0, 0, -200, 40, 3.4),
    slab(0, 44, -210, 22, 8, 'plate', 1.0),
    slab(0, 44, -190, 22, 8, 'plate', 1.0),
    slab(-8, 44, -200, 8, 12, 'plate', 1.0),
    slab(8, 44, -200, 8, 12, 'plate', 1.0),
    ...edgeLights(0, 44, -210, 22, 8, 'cyan'),
    ...edgeLights(0, 44, -190, 22, 8, 'cyan'),
    checkpoint(0, 44, -190),
    pad(0, 44, -190, 50, [0, 0.5, -0.86]),

    // ---- long haul
    ...deck(-292, 26, 30, 30),
    checkpoint(0, 26, -284),
    // Two segments with a doorway, not one full-width wall: the pad flight
    // from the lift tower threads the gap and lands ON the catwalk, and on
    // foot the doorway is the honest route to the shaft. Each segment is
    // still wide enough to take the exit portal the TURN AROUND sign sells.
    wallZ(-9.5, 26, -307.7, 13, 14, 'panel', 1.4),
    wallZ(9.5, 26, -307.7, 13, 14, 'panel', 1.4),
    neon(0, 40.2, -307.0, [30, 0.16, 0.16], 'cyan'),
    slab(0, 26, -314.2, 30, 14.5, 'plate', 1.2),   // catwalk to the shaft mouth

    // ---- drop chamber: the north wall stops low so the fling clears it
    wallZ(0, -46, -320.75, 34, 72, 'plate', 1.5),
    wallZ(0, -46, -344.75, 34, 56, 'plate', 1.5),
    wallX(-16.75, -46, -332.75, 24, 72, 'plate', 1.5),
    wallX(16.75, -46, -332.75, 24, 72, 'plate', 1.5),
    slab(0, -46, -332.75, 32, 24, 'panel', 1.5),
    neon(0, -45.4, -332.75, [30, 0.18, 0.18], 'magenta'),
    // Read on the way down. The floor shot is a blind sliver from the lip,
    // so the fair route is: portal the wall from the catwalk, hop in, portal
    // the floor AT YOUR FEET (the uplink lesson), walk out, then dive back.
    // z -343.9: at -344.5 the sign sat half a metre INSIDE the chamber's
    // far wall (face at -344.0), occluded by the wall it was meant to hang on.
    sign(0, -39, -343.9, 0, ['PORTAL THE FLOOR AT YOUR FEET', 'WALK THROUGH, THEN DIVE BACK IN'], '#ffb020'),

    // ---- exit bay
    slab(0, -50, -420, 56, 96, 'plate', 2),
    ...edgeLights(0, -50, -420, 56, 96, 'lime'),
    wallZ(0, -50, -469, 58, 26, 'plate', 2),
    wallX(-29, -50, -420, 96, 12, 'plate', 2),
    wallX(29, -50, -420, 96, 12, 'plate', 2),
    checkpoint(0, -50, -392),
    goal(0, -50, -436, 0),

    // ---- guidance
    sign(0, 3.4, 4, 0, [
      'VELOCITY',
      'PADS LAUNCH, PORTALS CUT CORNERS',
      'CLOCK STARTS NOW',
    ]),
    sign(0, 30.4, -300, 0, ['CATWALK AHEAD', 'THE SHAFT FLOOR TAKES A PORTAL'], '#ffb020'),
    sign(0, 30.4, -316, 180, ['TURN AROUND', 'THAT WALL FACES THE EXIT'], '#2ff3ff'),
  ],
};
